import type { FAILED_PAYMENTS_STRINGS } from './failed-payments-strings';
import type { ConsumerFailedPaymentModel } from './failed-payments.types';

export type ResolutionOption =
  | 'retry_today'
  | 'schedule_later'
  | 'change_payment_method'
  | 'unable_to_pay'
  | 'withholding'
  | 'deduct_from_rent'
  | 'split_it'
  | 'dispute_charge';

export type ActionableResolutionOption = ResolutionOption;

type FailedPaymentsStringKey = keyof typeof FAILED_PAYMENTS_STRINGS;

export type FailedPaymentsAudience = {
  key: 'resident' | 'homeowner';
  paymentPreference: 'Rent' | 'Maintenance';
  options: ResolutionOption[];
  optionLabelKeys: Record<ResolutionOption, FailedPaymentsStringKey>;
  lateFeeDescriptionKey: FailedPaymentsStringKey;
};

const RESIDENT_OPTION_LABEL_KEYS: FailedPaymentsAudience['optionLabelKeys'] = {
  retry_today: 'option.retry_today',
  schedule_later: 'option.schedule_later',
  change_payment_method: 'option.change_payment_method',
  unable_to_pay: 'option.unable_to_pay',
  withholding: 'option.withholding',
  deduct_from_rent: 'option.homeowner.deduct_from_rent',
  split_it: 'option.homeowner.split_it',
  dispute_charge: 'option.homeowner.dispute_charge',
};

const HOMEOWNER_OPTION_LABEL_KEYS: FailedPaymentsAudience['optionLabelKeys'] = {
  ...RESIDENT_OPTION_LABEL_KEYS,
  retry_today: 'option.homeowner.retry_today',
  schedule_later: 'option.homeowner.schedule_later',
  change_payment_method: 'option.homeowner.change_payment_method',
};

export const RESIDENT_AUDIENCE: FailedPaymentsAudience = {
  key: 'resident',
  paymentPreference: 'Rent',
  options: [
    'retry_today',
    'schedule_later',
    'change_payment_method',
    'unable_to_pay',
    'withholding',
  ],
  optionLabelKeys: RESIDENT_OPTION_LABEL_KEYS,
  lateFeeDescriptionKey: 'schedule_later.late_fee_description',
};

export const HOMEOWNER_AUDIENCE: FailedPaymentsAudience = {
  key: 'homeowner',
  paymentPreference: 'Maintenance',
  options: [
    'retry_today',
    'schedule_later',
    'change_payment_method',
    'deduct_from_rent',
    'split_it',
    'dispute_charge',
  ],
  optionLabelKeys: HOMEOWNER_OPTION_LABEL_KEYS,
  lateFeeDescriptionKey: 'schedule_later.late_fee_description_homeowner',
};

export function getVisibleResolutionOptions(
  audience: FailedPaymentsAudience,
  failedPayments: ConsumerFailedPaymentModel[],
): ResolutionOption[] {
  return audience.options.filter((option) => {
    if (option === 'deduct_from_rent') {
      return failedPayments.every((payment) => payment.canDeductFromRent);
    }
    if (option === 'split_it') {
      return failedPayments.every((payment) => payment.canSplitIt);
    }
    return true;
  });
}
